import React from 'react';
import { Clock, DollarSign, AlertTriangle, CheckCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';

const ShiftHistoryTable = ({ shifts, onViewDetail }) => {
  const { language } = useApp();

  if (!shifts || shifts.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400">
        <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
        <p>{language === 'en' ? 'No shift history' : language === 'km' ? 'គ្មានប្រវត្តិវេន' : '暂无班次历史'}</p>
      </div>
    );
  }

  const getDuration = (startTs, endTs) => {
    if (!startTs) return '-';
    const end = endTs || Math.floor(Date.now() / 1000);
    const diff = end - startTs;
    if (diff < 0) return '-';
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    return `${hours}${language === 'en' ? 'h' : language === 'km' ? 'ម៉ោង' : '小时'} ${minutes}${language === 'en' ? 'm' : language === 'km' ? 'នាទី' : '分钟'}`;
  };

  const formatTs = (ts) => {
    if (!ts) return '-';
    return new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (ts) => {
    if (!ts) return '-';
    return new Date(ts * 1000).toLocaleDateString();
  };

  const getDifference = (shift) => {
    const expected = parseFloat(shift.starting_cash || 0) + parseFloat(shift.total_sales || 0);
    const actual = parseFloat(shift.ending_cash || 0);
    return actual - expected;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Date' : language === 'km' ? 'កាលបរិច្ឆេទ' : '日期'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Cashier' : language === 'km' ? 'អ្នកគិតលុយ' : '收银员'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Duration' : language === 'km' ? 'រយៈពេល' : '时长'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Sales' : language === 'km' ? 'ការលក់' : '销售额'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Trans.' : language === 'km' ? 'ប្រតិបត្តិការ' : '交易数'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Cash' : language === 'km' ? 'ទឹកប្រាក់' : '现金'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Status' : language === 'km' ? 'ស្ថានភាព' : '状态'}
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              {language === 'en' ? 'Detail' : language === 'km' ? 'លម្អិត' : '明细'}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {shifts.map((shift) => {
            const difference = getDifference(shift);
            const isBalanced = Math.abs(difference) < 0.01;

            return (
              <tr key={shift.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="text-gray-900">{formatDate(shift.start_ts)}</div>
                  <div className="text-gray-500 text-xs">
                    {formatTs(shift.start_ts)}
                    {shift.end_ts ? ` - ${formatTs(shift.end_ts)}` : ''}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {shift.cashier_name || 'N/A'}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {shift.status === 'active'
                    ? <span className="text-blue-600">{getDuration(shift.start_ts, null)}</span>
                    : getDuration(shift.start_ts, shift.end_ts)
                  }
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center text-green-600 font-medium">
                    <DollarSign className="w-4 h-4 mr-1" />
                    {parseFloat(shift.total_sales || 0).toFixed(2)}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {shift.order_count || 0}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="text-gray-600">
                    {language === 'en' ? 'Open' : '期初'}: ${parseFloat(shift.starting_cash || 0).toFixed(2)}
                  </div>
                  <div className="text-gray-900 font-medium">
                    {language === 'en' ? 'Close' : '期末'}: ${parseFloat(shift.ending_cash || 0).toFixed(2)}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  {shift.status === 'active' ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                      {language === 'en' ? 'Active' : '进行中'}
                    </span>
                  ) : isBalanced ? (
                    <div className="flex items-center text-green-600 text-sm">
                      <CheckCircle className="w-4 h-4 mr-1" />
                      <span>{language === 'en' ? 'Balanced' : '平衡'}</span>
                    </div>
                  ) : (
                    <div className={`flex items-center text-sm ${difference > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      <span>{difference > 0 ? '+' : ''}{difference.toFixed(2)}</span>
                    </div>
                  )}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => onViewDetail && onViewDetail(shift.id)}
                    className="text-blue-600 hover:text-blue-800 hover:underline text-xs">
                    {language === 'en' ? 'View' : '查看'}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ShiftHistoryTable;